<script lang="ts" setup>
import {computed, onBeforeUnmount, ref, watch} from "vue";
import {useDisplay} from "vuetify";
import {useRoute} from "vue-router";
import CompanyApi from "@/api/company.api.ts";
import DetailsItem from "@/components/Cards/Details/DetailsItem.vue";
import type {CompanyEmployee, CompanyEmployeeAddConflictResponse, CompanyEmployeeAddFailureResponse, CompanyEmployeeCandidate, CompanyEmployeeRow} from "@/domain/type/company.api.type.ts";
import {Source} from "@/domain/type/source.type.ts";
import {RouteEnum} from "@/router/RouteEnum.ts";
import {SynchroType} from "@/domain/type/synchro.type.ts";
import router from "@/router/router.ts";

type PresenceFilter = 'all' | 'yes' | 'no'
type BulkAddResult = {total: number, processed: number, succeeded: number, failed: number, cancelled: boolean}

const route = useRoute()
function normalizeSourceParameter(value: unknown): string | null {
  const parameter = Array.isArray(value) ? value[0] : value
  if (typeof parameter !== 'string' || '' === parameter.trim()) return null

  return parameter.trim().toLowerCase()
}

const sourceParameter = computed(() => normalizeSourceParameter(route.query.source))
const targetParameter = computed(() => normalizeSourceParameter(route.query.target))
const sourceLabel = computed(() => sourceParameter.value?.toUpperCase() ?? 'non définie')
const targetLabel = computed(() => targetParameter.value?.toUpperCase() ?? 'non définie')
const supportedCombination = computed(() => sourceParameter.value === Source.EVA && targetParameter.value === Source.AMMON)
const unsupportedCombinationMessage = computed(() => supportedCombination.value
  ? null
  : `La combinaison ${sourceLabel.value} → ${targetLabel.value} n'est pas prise en charge. Pour le moment, seule la combinaison EVA → AMMON est disponible.`)
const users = ref<CompanyEmployeeRow[]>([])
const presenceFilter = ref<PresenceFilter>('all')
const search = ref('')
const loadingEmployees = ref(true)
const matching = ref(false)
const processedEmployees = ref(0)
const addingEmployeeId = ref<number | null>(null)
const bulkAdding = ref(false)
const bulkStopRequested = ref(false)
const showBulkAddConfirmation = ref(false)
const bulkProcessedEmployees = ref(0)
const bulkTotalEmployees = ref(0)
const bulkAddResult = ref<BulkAddResult | null>(null)
const addErrors = ref<Record<number, string>>({})
const error = ref<string | null>(null)
const targetCompanyMissing = ref(false)
let requestGeneration = 0
const addErrorTimeouts = new Map<number, ReturnType<typeof setTimeout>>()
const batchSize = 100
const bulkAddConcurrency = 10
const matchProgress = computed(() => users.value.length === 0 ? 100 : Math.round(processedEmployees.value / users.value.length * 100))
const bulkAddProgress = computed(() => bulkTotalEmployees.value === 0 ? 0 : Math.round(bulkProcessedEmployees.value / bulkTotalEmployees.value * 100))
const employeesToAdd = computed(() => users.value.filter(employee => employee.inAmmon !== true && !employee.ambiguous))
const absentEmployeesCount = computed(() => users.value.filter(employee => employee.inAmmon === false && !employee.ambiguous).length)
const presentEmployeesCount = computed(() => users.value.filter(employee => employee.inAmmon === true).length)
const filteredUsers = computed(() => {
  const query = normalizeSearch(search.value)

  return users.value.filter(employee => {
    const matchesPresence = presenceFilter.value === 'all'
      || (presenceFilter.value === 'yes' && employee.inAmmon === true)
      || (presenceFilter.value === 'no' && employee.inAmmon === false && !employee.ambiguous)
    const matchesSearch = '' === query || [
      employee.firstName,
      employee.lastName,
      employee.email,
      `${employee.firstName ?? ''} ${employee.lastName ?? ''}`,
      `${employee.lastName ?? ''} ${employee.firstName ?? ''}`,
    ].some(value => normalizeSearch(value).includes(query))

    return matchesPresence && matchesSearch
  })
})
const presenceFilterItems = computed<{title: string, value: PresenceFilter}[]>(() => [
  {title: `Tous (${users.value.length})`, value: 'all'},
  {title: `Présent (${presentEmployeesCount.value})`, value: 'yes'},
  {title: `Absent (${absentEmployeesCount.value})`, value: 'no'},
])
const searchLabel = computed(() => `Recherche (${filteredUsers.value.length})`)
const sideCols = computed(() => {
  const {sm} = useDisplay()
  return sm.value ? 5 : 3
})
const centerCols = computed(() => {
  const {md, lg, xl, xxl} = useDisplay()
  return md.value ? 4 : lg ? 4 : xl ? 2 : xxl ? 1 : 4
})
function clearAddErrorTimeouts(): void {
  for (const timeout of addErrorTimeouts.values()) clearTimeout(timeout)
  addErrorTimeouts.clear()
}

function dismissAddError(employeeId: number): void {
  const timeout = addErrorTimeouts.get(employeeId)
  if (timeout) clearTimeout(timeout)
  addErrorTimeouts.delete(employeeId)

  const errors = {...addErrors.value}
  delete errors[employeeId]
  addErrors.value = errors
}

function showAddError(employeeId: number, message: string): void {
  dismissAddError(employeeId)
  addErrors.value = {...addErrors.value, [employeeId]: message}
  addErrorTimeouts.set(employeeId, setTimeout(() => {
    const errors = {...addErrors.value}
    delete errors[employeeId]
    addErrors.value = errors
    addErrorTimeouts.delete(employeeId)
  }, 6500))
}

onBeforeUnmount(clearAddErrorTimeouts)

watch(() => [route.params.siret, route.query.source, route.query.target], async () => {
  const generation = ++requestGeneration
  const siret = String(route.params.siret)
  users.value = []
  presenceFilter.value = 'all'
  search.value = ''
  loadingEmployees.value = true
  matching.value = false
  processedEmployees.value = 0
  bulkAdding.value = false
  bulkStopRequested.value = false
  showBulkAddConfirmation.value = false
  bulkProcessedEmployees.value = 0
  bulkTotalEmployees.value = 0
  bulkAddResult.value = null
  error.value = null
  targetCompanyMissing.value = false
  clearAddErrorTimeouts()
  addErrors.value = {}
  addingEmployeeId.value = null

  if (!supportedCombination.value) {
    loadingEmployees.value = false
    return
  }

  try {
    const response = await CompanyApi.getEmployees(siret, sourceParameter.value as Source)
    if (generation !== requestGeneration) return
    users.value = response.data.employees.map(employee => ({...employee, ammonLink: null, inAmmon: null, ambiguous: false, matchedBy: null, ammonEmployee: null}))
    loadingEmployees.value = false
    await matchEmployees(siret, generation)
  } catch (exception: unknown) {
    if (generation !== requestGeneration) return
    const status = (exception as {response?: {status?: number}})?.response?.status
    error.value = status === 404
      ? "Société EVA introuvable pour ce SIRET."
      : "Impossible de charger les utilisateurs de cette société."
  } finally {
    if (generation === requestGeneration) loadingEmployees.value = false
  }
}, {immediate: true})

async function matchEmployees(siret: string, generation: number): Promise<void> {
  if (users.value.length === 0) return
  matching.value = true
  error.value = null
  targetCompanyMissing.value = false
  try {
    for (let offset = 0; offset < users.value.length; offset += batchSize) {
      const batch = users.value.slice(offset, offset + batchSize)
      const candidates: CompanyEmployeeCandidate[] = batch.map(({id, firstName, lastName, birthDate, email}) => ({id, firstName, lastName, birthDate, email}))
      const response = await CompanyApi.matchEmployees(siret, candidates, targetParameter.value as Source)
      if (generation !== requestGeneration) return
      const matches = new Map(response.data.matches.map(match => [match.id, match]))
      users.value = users.value.map(employee => {
        const match = matches.get(employee.id)
        return match ? {...employee, inAmmon: match.inAmmon, ambiguous: match.ambiguous, matchedBy: match.matchedBy, ammonLink: match.ammonLink, ammonEmployee: match.ammonEmployee} : employee
      })
      processedEmployees.value = Math.min(offset + batch.length, users.value.length)
    }
  } catch (exception: unknown) {
    if (generation === requestGeneration) {
      const status = (exception as {response?: {status?: number}})?.response?.status
      if (status === 404) {
        targetCompanyMissing.value = true
        error.value = "Société AMMON introuvable pour ce SIRET."
      } else {
        error.value = `Le rapprochement AMMON a échoué après ${processedEmployees.value} employés.`
      }
    }
  } finally {
    if (generation === requestGeneration) matching.value = false
  }
}

function retryMatching(): void {
  if (!supportedCombination.value) return

  const generation = ++requestGeneration
  processedEmployees.value = 0
  error.value = null
  targetCompanyMissing.value = false
  clearAddErrorTimeouts()
  addErrors.value = {}
  users.value = users.value.map(employee => ({...employee, inAmmon: null, ambiguous: false, matchedBy: null, ammonEmployee: null}))
  void matchEmployees(String(route.params.siret), generation)
}

async function addEmployee(
  employee: CompanyEmployeeRow,
  siret: string = String(route.params.siret),
  generation: number = requestGeneration,
  allowConcurrent: boolean = false,
): Promise<boolean> {
  if (generation !== requestGeneration || matching.value || targetCompanyMissing.value || (!allowConcurrent && null !== addingEmployeeId.value) || employee.inAmmon === true) return false

  if (!bulkAdding.value) bulkAddResult.value = null
  addingEmployeeId.value = employee.id
  dismissAddError(employee.id)
  try {
    const response = await CompanyApi.addEmployee(siret, employee.id, sourceParameter.value as Source, targetParameter.value as Source)
    if (generation !== requestGeneration) return false
    users.value = users.value.map(row => row.id === employee.id
      ? {...row, inAmmon: true, ambiguous: false, matchedBy: response.data.matchedBy, ammonLink: response.data.ammonLink, ammonEmployee: response.data.employee}
      : row)
    return true
  } catch (exception: unknown) {
    if (generation !== requestGeneration) return false
    const response = (exception as {
      response?: {
        status?: number,
        data?: Partial<CompanyEmployeeAddConflictResponse & CompanyEmployeeAddFailureResponse>,
      },
    })?.response
    const matches = response?.data?.matches
    const message = response?.status === 409 && Array.isArray(matches)
      ? `Plusieurs utilisateurs AMMON correspondent : ${matches.map(match => `${fullName(match) || `Utilisateur ${match.id}`} (ID ${match.id}${match.email ? `, ${match.email}` : ''})`).join(' ; ')}`
      : response?.status === 400 && Array.isArray(response.data?.errors) && response.data.errors.length > 0
        ? formatEmployeeAddValidationErrors(response.data.errors)
        : response?.status === 400
          ? `Requête invalide : ${response.data?.error ?? "les données de l'employé ne sont pas acceptées."}`
          : response?.data?.error ?? "Impossible d'ajouter cet utilisateur dans AMMON."
    showAddError(employee.id, message)
    return false
  } finally {
    if (generation === requestGeneration) addingEmployeeId.value = null
  }
}

async function addAllEmployees(): Promise<void> {
  if (bulkAdding.value || matching.value || targetCompanyMissing.value || null !== addingEmployeeId.value) return

  const employees = employeesToAdd.value
  if (employees.length === 0) return

  const siret = String(route.params.siret)
  const generation = requestGeneration
  let succeeded = 0
  let failed = 0
  bulkAdding.value = true
  bulkStopRequested.value = false
  bulkProcessedEmployees.value = 0
  bulkTotalEmployees.value = employees.length
  bulkAddResult.value = null

  try {
    let nextEmployeeIndex = 0
    const addNextEmployee = async (): Promise<void> => {
      while (nextEmployeeIndex < employees.length) {
        if (generation !== requestGeneration || bulkStopRequested.value) return
        const employee = employees[nextEmployeeIndex]
        if (!employee) return
        nextEmployeeIndex++
        const added = await addEmployee(employee, siret, generation, true)
        if (generation !== requestGeneration) return

        if (added) {
          succeeded++
        } else {
          failed++
        }
        bulkProcessedEmployees.value++
      }
    }
    const workerCount = Math.min(bulkAddConcurrency, employees.length)
    await Promise.all(Array.from({length: workerCount}, () => addNextEmployee()))

    if (generation === requestGeneration) {
      bulkAddResult.value = {
        total: employees.length,
        processed: bulkProcessedEmployees.value,
        succeeded,
        failed,
        cancelled: bulkStopRequested.value,
      }
    }
  } finally {
    if (generation === requestGeneration) bulkAdding.value = false
  }
}

function stopBulkAdd(): void {
  bulkStopRequested.value = true
}
function confirmAddAllEmployees(): void {
  showBulkAddConfirmation.value = false
  void addAllEmployees()
}
function fullName(user: Pick<CompanyEmployee, 'firstName' | 'lastName'>): string {
  return [user.firstName, user.lastName].filter(Boolean).join(' ')
}
function formatEmployeeAddValidationErrors(errors: CompanyEmployeeAddFailureResponse['errors']): string {
  const fieldLabels: Record<string, string> = {
    employee: 'Employé',
    healthCareReference: 'Numéro de sécurité sociale',
    identity: 'Identité',
  }

  return `Ajout impossible : ${errors
    .map(({field, message}) => {
      const label = fieldLabels[field] ?? field
      const detail = message === 'Must be 20 characters or fewer.'
        ? '20 caractères maximum.'
        : message

      return `${label} : ${detail}`
    })
    .join(' ; ')}`
}

function employeeFusionRoute(employee: CompanyEmployeeRow) {
  return {
    name: RouteEnum.FusionFromOwner,
    params: {
      sourceLeft: sourceParameter.value ?? '',
      sourceRight: targetParameter.value ?? '',
      ownerId: String(route.params.siret),
      id: String(employee.id),
      type: SynchroType.PERSONS,
    },
  }
}

function openFusion(employee: CompanyEmployeeRow): void {
  const resolvedRoute = router.resolve(employeeFusionRoute(employee))
  window.open(resolvedRoute.fullPath, '_blank')
}
function normalizeSearch(value: string | null): string {
  return (value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLocaleLowerCase('fr')
}
function openLink(link: string | null): void {
  if (link) window.open(link, '_blank', 'noopener,noreferrer')
}

</script>

<template>
  <v-container class="company-users-view">
    <v-row align="center" class="mt-1 d-none d-sm-none d-md-flex" justify="center">
          <v-col :cols="sideCols" class="d-flex justify-end">
            <img alt="Source EVA" class="source-logo" src="@/assets/img/eva.png" style="width: 100px; margin-right: 100px;" />
          </v-col>
          <v-col :cols="centerCols" class="d-flex justify-center align-center flex-column position-relative">
            <v-container v-if="loadingEmployees || matching || bulkAdding" class="match-progress">
              <div v-if="loadingEmployees" class="text-body-2 mt-2">
                Chargement des utilisateurs {{ sourceLabel }}…
              </div>
              <div v-else-if="matching" class="text-body-2 mt-2">
                Rapprochement AMMON : {{ processedEmployees }} / {{ users.length }}
              </div>
              <template v-else>
                <div class="d-flex align-center justify-space-between text-body-2 mt-2">
                  <span>
                    {{ bulkStopRequested ? "Arrêt des nouveaux ajouts…" : `Ajout dans AMMON : ${bulkProcessedEmployees} / ${bulkTotalEmployees}` }}
                  </span>
                  <v-btn
                    :disabled="bulkStopRequested"
                    size="small"
                    text="Arrêter"
                    variant="text"
                    @click="stopBulkAdd"
                  />
                </div>
              </template>
              <v-progress-linear
                :indeterminate="loadingEmployees"
                :model-value="bulkAdding ? bulkAddProgress : matchProgress"
                class="mt-3"
                color="primary"
                height="10"
                rounded
              />
            </v-container>
            <v-container v-else>
              <v-row no-gutters width="100%">
                <v-col cols="8" lg="7" md="12">
                  <v-text-field
                    v-model="search"
                    :style="{ background: 'white' }"
                    density="compact"
                    hide-details
                    :label="searchLabel"
                    prepend-inner-icon="mdi-magnify"
                    variant="filled"
                    width="100%"
                  />
                </v-col>
                <v-col cols="3" lg="4" md="10">
                  <v-select
                    v-model="presenceFilter"
                    :items="presenceFilterItems"
                    density="compact"
                    hide-details
                    item-title="title"
                    item-value="value"
                    variant="filled"
                  />
                </v-col>
                <v-col cols="1" lg="1" md="2" class="d-flex justify-start">
                  <v-btn
                    v-tooltip="`Ajouter tous les utilisateurs éligibles dans AMMON (${employeesToAdd.length})`"
                    :aria-label="`Ajouter tous les utilisateurs éligibles dans AMMON (${employeesToAdd.length})`"
                    :disabled="matching || targetCompanyMissing || bulkAdding || addingEmployeeId !== null || employeesToAdd.length === 0"
                    :loading="bulkAdding"
                    color="primary"
                    icon="mdi-account-multiple-plus"
                    size="small"
                    variant="flat"
                    @click="showBulkAddConfirmation = true"
                  />
                </v-col>
              </v-row>
            </v-container>
          </v-col>
          <v-col :cols="sideCols" class="d-flex justify-start">
            <img alt="Cible AMMON" class="source-logo" src="@/assets/img/ammon.png" style="width: 110px; margin-left: 80px;" />
          </v-col>
    </v-row>
    <v-row align="center" class="mt-1 d-sm-flex d-md-none" justify="center">
      <v-col class="text-center">
        <p>Écran trop petit pour accéder à l'interface de synchronisation.</p>
      </v-col>
    </v-row>

    <v-alert v-if="unsupportedCombinationMessage" type="warning" variant="tonal" class="mb-4">
      {{ unsupportedCombinationMessage }}
    </v-alert>
    <v-alert v-if="error" type="error" variant="tonal" class="mb-4">
      {{ error }}
      <v-btn v-if="!loadingEmployees && users.length > 0" class="ml-3" size="small" variant="text" @click="retryMatching">Réessayer</v-btn>
    </v-alert>
    <v-alert v-if="supportedCombination && !loadingEmployees && !error && users.length === 0" type="info" variant="tonal">
      Aucun utilisateur {{ sourceLabel }} trouvé pour cette société.
    </v-alert>


    <v-alert v-if="supportedCombination && bulkAddResult && !bulkAddResult.cancelled" class="mb-4" :type="bulkAddResult.failed > 0 ? 'warning' : 'success'" variant="tonal">
      Ajout terminé : {{ bulkAddResult.succeeded }} utilisateur{{ bulkAddResult.succeeded > 1 ? 's' : '' }} traité{{ bulkAddResult.succeeded > 1 ? 's' : '' }} avec succès sur {{ bulkAddResult.total }}.
      <span v-if="bulkAddResult.failed > 0">{{ bulkAddResult.failed }} en erreur ; consultez les lignes concernées.</span>
    </v-alert>

    <v-virtual-scroll
      v-if="supportedCombination && !loadingEmployees && filteredUsers.length > 0"
      :items="filteredUsers"
      :item-height="300"
      renderless
      v-slot="{ item: user, itemRef }"
    >
        <v-row
          :ref="itemRef"
          align="stretch"
          class="virtual-user-row d-none d-sm-none d-md-flex cards-row"
          justify="center"
        >
          <v-col :cols="sideCols" class="d-flex justify-end">
            <v-card class="user-card source-card" style="width: 300px;" elevation="2" @click="openLink(user.evaLink)">
              <v-card-text>
                <details-item label="Nom" :value="user.lastName ?? '—'" />
                <details-item label="Prénom" :value="user.firstName ?? '—'" />
                <details-item label="Email" :value="user.email ?? '—'" />
                <details-item label="Date de naissance" :value="user.birthDate ?? '—'" />
                <details-item label="ID" :value="user.externalReference ?? user.id" />
              </v-card-text>
            </v-card>
          </v-col>
          <v-col :cols="centerCols" class="d-flex justify-center align-center flex-column position-relative">
            <v-btn v-if="user.inAmmon === true" style="width:180px;" @click="openFusion(user)">
              Vérifier
            </v-btn>
            <v-btn
              v-else
              :disabled="matching || targetCompanyMissing || addingEmployeeId !== null || bulkAdding"
              :loading="addingEmployeeId === user.id"
              style="width:180px;"
              @click="addEmployee(user)"
            >
              Ajouter
            </v-btn>
            <v-fade-transition>
              <v-alert
                v-if="addErrors[user.id]"
                class="add-error-alert"
                closable
                density="compact"
                type="error"
                variant="tonal"
                @click:close="dismissAddError(user.id)"
              >
                {{ addErrors[user.id] }}
              </v-alert>
            </v-fade-transition>
          </v-col>
          <v-col :cols="sideCols" class="d-flex justify-start">
            <v-card
              class="user-card target-card"
              style="width: 300px;"
              :class="{ 'target-card--disabled': user.inAmmon !== true }"
              :aria-busy="matching && user.inAmmon === null"
              elevation="2"
              @click="openLink(user.ammonLink)"
            >
              <v-card-text v-if="matching && user.inAmmon === null" class="target-card-loader">
                <v-progress-circular
                  aria-label="Rapprochement en cours"
                  color="primary"
                  indeterminate
                  size="32"
                />
              </v-card-text>
              <v-card-text v-else>
                <template v-if="user.ammonEmployee">
                  <details-item label="Nom" :value="user.ammonEmployee.lastName ?? '—'" />
                  <details-item label="Prénom" :value="user.ammonEmployee.firstName ?? '—'" />
                  <details-item label="Email" :value="user.ammonEmployee.email ?? '—'" />
                  <details-item label="Date de naissance" :value="user.ammonEmployee.birthDate ?? '—'" />
                  <details-item label="ID" :value="user.ammonEmployee.id" />
                </template>
              </v-card-text>
              <v-overlay
                :model-value="user.inAmmon === false && !user.ambiguous"
                :opacity="0.6"
                class="align-center justify-center"
                contained
              >
                <div class="d-flex flex-column">
                  <span class="text-white font-weight-bold text-center pa-5">Aucune correspondance trouvée.</span>
                </div>
              </v-overlay>
            </v-card>
          </v-col>
        </v-row>
    </v-virtual-scroll>
    <v-alert v-if="supportedCombination && !loadingEmployees && users.length > 0 && filteredUsers.length === 0" type="info" variant="tonal">
      Aucun utilisateur ne correspond à ces filtres.
    </v-alert>
    <v-dialog v-model="showBulkAddConfirmation" max-width="520">
      <v-card>
        <v-card-title class="text-h6">Confirmer l’ajout en série</v-card-title>
        <v-card-text>
          Ajouter {{ employeesToAdd.length }} utilisateur{{ employeesToAdd.length > 1 ? 's' : '' }} éligible{{ employeesToAdd.length > 1 ? 's' : '' }} dans AMMON ?
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text="Annuler" variant="text" @click="showBulkAddConfirmation = false" />
          <v-btn
            color="primary"
            :disabled="employeesToAdd.length === 0 || matching || targetCompanyMissing || bulkAdding || addingEmployeeId !== null"
            text="Confirmer"
            variant="flat"
            @click="confirmAddAllEmployees"
          />
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<style scoped>
.match-progress {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.source-logo {
  width: 92px;
  max-height: 46px;
  object-fit: contain;
}

.virtual-user-row {
  padding-top: 48px;
}
.add-error-alert {
  position: absolute;
  z-index: 10;
  top: calc(100% + 8px);
  left: 50%;
  width: max-content;
  max-width: min(420px, 80vw);
  transform: translateX(-50%);
}

.user-card {
  transition: transform 160ms ease, box-shadow 160ms ease;
  cursor: pointer;
}

.user-card:hover {
  transform: translateY(-2px);
}
.user-card :deep(.cardLine) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.target-card {
  position: relative;
}

.target-card-loader {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}


.target-card--disabled {
  cursor: default;
  filter: grayscale(1);
  opacity: 0.58;
}

</style>
